/** @format */

import {Chart, CommonChartData} from 'components/charts/multiline-chart/model/charts';

import {roundFloatNumber, safeDivide} from 'helpers/numberHelpers';

import {ChartIds} from '../../../models/chartIds';
import {GraphComplexValuesList} from '../../../models/common';

type BasicMetric = 'clicks' | 'impressions' | 'shows';

interface BuildCrSeriesOptions {
  orderedPointNames: ReadonlyArray<string>;
  aId: number | string;
  bId: number | string;
  graphComplexValues: GraphComplexValuesList;
  aChart?: Chart<ChartIds>;
  bChart?: Chart<ChartIds>;
  yesterdayIndex: number;
}

const normalizeBasicKey = (key: BasicMetric): Exclude<BasicMetric, 'shows'> =>
  key === 'shows' ? 'impressions' : key;

const isBasicMetric = (key: number | string): boolean =>
  key === 'clicks' || key === 'impressions' || key === 'shows';

export const buildCrSeries = ({
  orderedPointNames,
  aId,
  bId,
  graphComplexValues,
  aChart,
  bChart,
  yesterdayIndex,
}: BuildCrSeriesOptions): CommonChartData[] => {
  return orderedPointNames.map((pointName, index) => {
    let numerator: number;
    let denominator: number;

    if (isBasicMetric(aId)) {
      const pointData = graphComplexValues[pointName];
      const key = normalizeBasicKey(aId as BasicMetric);
      numerator = pointData?.values[key] || 0;
    } else {
      numerator = aChart?.chartsData[index]?.primary ?? 0;
    }

    if (isBasicMetric(bId)) {
      const pointData = graphComplexValues[pointName];
      const key = normalizeBasicKey(bId as BasicMetric);
      denominator = pointData?.values[key] || 0;
    } else {
      denominator = bChart?.chartsData[index]?.primary ?? 0;
    }

    const primaryFloat = safeDivide(numerator, denominator) * 100;
    const primary = roundFloatNumber(primaryFloat, 1);

    const point: CommonChartData = {name: pointName};

    if (index <= yesterdayIndex) {
      point.primary = primary;
    }

    const srcA = aChart?.chartsData[index];
    const srcB = bChart?.chartsData[index];

    if (srcA && srcB) {
      if (srcA.range && srcB.range) {
        const [aMin, aMax] = srcA.range;
        const [bMin, bMax] = srcB.range;
        const min = safeDivide(aMin, bMax) * 100;
        const max = safeDivide(aMax, bMin) * 100;
        if (Number.isFinite(min) && Number.isFinite(max)) {
          point.range = [min, max];
          point.average = (min + max) / 2;
        }
      } else if (srcA.average !== undefined && srcB.average !== undefined && srcB.average !== 0) {
        point.average = safeDivide(srcA.average, srcB.average) * 100;
      }
    }

    return point;
  });
};
